import { useEffect, useState } from 'react';

import { getFinancials, type PlatformFinancials } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatMoney } from '../../lib/format';
import './AdminPages.css';

/**
 * Platform financial dashboard. Read-only, aggregate-only.
 *
 * Two blocks, kept visually apart because they mean different things: GMV is
 * money taken to date across completed orders, while the fee breakdown only
 * exists for events that have been reconciled. Merging them would imply a
 * platform revenue figure the data does not support.
 */
function Stat({
  label, value, sub, tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'gold' | 'success' | 'violet';
}) {
  return (
    <div className={`adm-metric${tone ? ` adm-metric--${tone}` : ''}`}>
      <span className="adm-metric__label">{label}</span>
      <span className="adm-metric__value mono">{value}</span>
      {sub && <span className="adm-metric__sub">{sub}</span>}
    </div>
  );
}

export function AdminFinancials() {
  const [data, setData] = useState<PlatformFinancials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getFinancials()
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load financials.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="adm-status">Loading…</p>;
  if (error) return <p className="adm-status adm-error" role="alert">{error}</p>;
  if (!data) return null;

  const scanRate = data.gmv.ticketsSold > 0
    ? Math.round((data.gmv.ticketsScanned / data.gmv.ticketsSold) * 100)
    : 0;

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Platform financials</h1>
          <p className="adm-lede">
            Aggregate only. No individual buyer appears in any figure on this page.
          </p>
        </div>
      </header>

      <section className="adm-section">
        <h2 className="adm-section__title">Gross merchandise value</h2>
        <p className="adm-section__note adm-muted">
          Money taken across every completed order, to date.
        </p>
        <div className="adm-metrics-grid">
          <Stat label="GMV" value={formatMoney(data.gmv.grossCents)} tone="gold" />
          <Stat label="Completed orders" value={String(data.gmv.completedOrders)} />
          <Stat label="Tickets sold" value={String(data.gmv.ticketsSold)} />
          <Stat
            label="Checked in"
            value={String(data.gmv.ticketsScanned)}
            sub={`${scanRate}% of tickets sold`}
          />
        </div>
      </section>

      <section className="adm-section">
        <h2 className="adm-section__title">Events</h2>
        <div className="adm-metrics-grid">
          <Stat label="Total" value={String(data.events.total)} />
          <Stat label="On sale" value={String(data.events.on_sale)} tone="success" />
          <Stat label="Draft" value={String(data.events.draft)} sub="pre-publish" />
          <Stat label="Closed" value={String(data.events.closed)} />
        </div>
      </section>

      <section className="adm-section">
        <h2 className="adm-section__title">Revenue by fee type</h2>
        {/* Stated plainly rather than buried: these are settled events only. */}
        <p className="adm-section__note adm-warn">{data.settled.note}</p>
        <div className="adm-metrics-grid">
          <Stat
            label="Reconciled events"
            value={String(data.settled.events)}
            sub="basis for the figures below"
          />
          <Stat label="Settled gross" value={formatMoney(data.settled.grossCents)} />
          <Stat
            label="Organizer platform fee"
            value={formatMoney(data.settled.platformFeeCents)}
            tone="gold"
            sub="Voleto revenue"
          />
          <Stat
            label="Stripe processing"
            value={formatMoney(data.settled.stripeFeeCents)}
            sub="absorbed by Voleto"
          />
          {/* This column holds NET PROCEEDS since task 51: the full amount transferred
              to the SPV, before its production costs and before the 80/20 split. Labelled
              for what it now is, rather than what it meant under the old waterfall. */}
          <Stat
            label="Net proceeds to SPVs"
            value={formatMoney(data.settled.netDistributableCents)}
            sub="transferred out in full"
            tone="success"
          />
        </div>
      </section>

      <section className="adm-section">
        <h2 className="adm-section__title">Investment</h2>
        <div className="adm-metrics-grid">
          <Stat label="Investments" value={String(data.investments.count)} />
          <Stat
            label="Capital invested"
            value={formatMoney(data.investments.investedCents)}
            tone="violet"
          />
          <Stat
            label="Platform entry fees"
            value={formatMoney(data.investments.platformEntryFeeCents)}
            tone="gold"
          />
        </div>
      </section>
    </div>
  );
}
